/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
// Extend From
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { service } from '@ember/service';
// External Imports
import moment from 'moment';
// Internal Imports
import ExpulsionStage from 'linced-app/enums/expulsion-stage';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { SuspensionCategory } from '@linc-technologies/linced-common/enums/suspensions';
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';

export default class SuspensionIndicator extends Component {
  @service
  session;

  @service
  store;

  @service
  toaster;

  learner = null;
  @tracked
  isSuspended = false;
  @tracked isExpelled = false;
  @tracked
  dueBack = '';

  async checkForSuspensionsAndExpulsions() {
    // This component is reused as the route model changes, so clear the last
    // learner's state before looking anything up - otherwise a suspended
    // learner's banner carries over to the next learner we render.
    this.isSuspended = false;
    this.isExpelled = false;
    this.dueBack = '';

    if (this.session.get('school.jurisdiction') !== Jurisdiction.AU_NSW) {
      return;
    }

    // The learner can change while the queries below are in flight; a response
    // that arrives for a previous learner must not write to our state.
    let learnerId = this.get('learner.id');
    let isStale = () => {
      return (
        this.isDestroyed ||
        this.isDestroying ||
        this.get('learner.id') !== learnerId
      );
    };

    // Pull both suspensions and expulsions
    try {
      let expulsions = await this.store.query('attendance/expulsion', {
        schoolId: this.get('session.activeSchool'),
        learnerId,
      });

      if (isStale()) {
        return;
      }

      let suspensions = await this.store.query('attendance/suspension', {
        schoolId: this.get('session.activeSchool'),
        learnerId,
      });

      if (isStale()) {
        return;
      }

      // Grab any instances where the kid was actually expelled (ideally just one)
      expulsions = expulsions.filter((expulsion) => {
        return expulsion.get('stage') === ExpulsionStage.EXPELLED;
      });

      if (expulsions.length > 0) {
        let latestExpulsion = expulsions[0];
        if (expulsions.length > 1) {
          expulsions.forEach((expulsion) => {
            let expulsionDate = latestExpulsion.get('expulsionStartDate');
            if (isEmpty(expulsion.get('expulsionStartDate'))) {
              return;
            }
            if (
              moment(expulsion.get('expulsionStartDate')).isAfter(
                moment(expulsionDate),
              )
            ) {
              latestExpulsion = expulsion;
            }
          });
        }

        this.isExpelled = true;
        this.dueBack = latestExpulsion.get('expulsionStartDate');
        return;
      }

      suspensions = suspensions.filter((suspension) => {
        return suspension.get('category') > SuspensionCategory.FORMAL_CAUTION;
      });

      // Suspensions from suspension records
      let suspensionDates = suspensions.map((suspension) => {
        return {
          startDate: suspension.get('startDate'),
          endDate: suspension.get('displayEndDate'),
          dueBack: suspension.get('nextSchoolDay'),
        };
      });

      let today = moment();

      // Check if today falls into any of the suspensions
      for (let suspensionDate of suspensionDates) {
        let startDay = moment(suspensionDate.startDate, 'YYYY-MM-DD');
        let endDay = moment(suspensionDate.endDate, 'YYYY-MM-DD');
        if (startDay.isSameOrBefore(today) && endDay.isSameOrAfter(today)) {
          this.isSuspended = true;

          this.dueBack = moment(suspensionDate.dueBack, 'YYYY-MM-DD').format(
            'ddd Do MMM',
          );
        }
      }
    } catch (e) {
      console.error(`There was an error loading suspension information: ${e}`);
    }
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    this.checkForSuspensionsAndExpulsions();
  }

  @action
  noop() {}
}
